import { FC } from 'react';

import { ShareChannelIcon } from '@service/giftStore/components/ShareChannelIcon';
import {
  GiftCouponSummary,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { CouponRow } from './CouponRow';
import { GiftItemEmbed } from './types';

interface CouponTableProps {
  sortedCoupons: GiftCouponSummary[];
  giftItemEmbedMap: Map<string, GiftItemEmbed>;
  canCancel: boolean;
  hasCancellableCoupons: boolean;
  disposeMode: boolean;
  selectedIds: Set<string>;
  disposeSelectedIds: Set<string>;
  isPromoting: boolean;
  /** 다른 쿠폰과 같은 카톡방으로 공유된 쿠폰 ID 집합 */
  crossCouponOverlapIds?: Set<string>;
  /** 구매일 / 만료·교환일 컬럼 숨김 */
  hideDates?: boolean;
  toggleSelect: (id: string) => void;
  toggleDisposeSelect: (id: string) => void;
  /** 재공유이력 칩 클릭 → 재공유 이력 모달 오픈 */
  onOpenShareHistory: (coupon: GiftCouponSummary) => void;
  handlePromoteToReissued: (couponId: string) => Promise<void>;
  onIssueRefund?: (couponId: string) => void;
  onIssueRenewal?: (couponId: string) => void;
  onCancelIssue?: (couponId: string) => void;
  onOpenKakaoShareCallbacks?: (coupon: GiftCouponSummary) => void;
  isIssuing?: boolean;
}

export const CouponTable: FC<CouponTableProps> = ({
  sortedCoupons,
  giftItemEmbedMap,
  canCancel,
  hasCancellableCoupons,
  disposeMode,
  selectedIds,
  disposeSelectedIds,
  isPromoting,
  crossCouponOverlapIds,
  hideDates,
  toggleSelect,
  toggleDisposeSelect,
  onOpenShareHistory,
  handlePromoteToReissued,
  onIssueRefund,
  onIssueRenewal,
  onCancelIssue,
  onOpenKakaoShareCallbacks,
  isIssuing,
}) => {
  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-left text-gray-500">
            {canCancel && hasCancellableCoupons && !disposeMode && (
              <th className="whitespace-nowrap px-3 py-2 font-medium">선택</th>
            )}
            {disposeMode && (
              <th className="whitespace-nowrap px-3 py-2 font-medium">폐기</th>
            )}
            <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">구분</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">상품</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">가격</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">교환상태</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
            <th
              className="whitespace-nowrap px-3 py-2 font-medium"
              title="카카오톡 공유 웹훅으로 집계된 채팅방 수 (클릭 시 상세 이력)"
            >
              카톡공유기록
            </th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유채널</th>
            {!hideDates && (
              <>
                <th className="whitespace-nowrap px-3 py-2 font-medium">구매일</th>
                <th className="whitespace-nowrap px-3 py-2 font-medium">만료/교환일</th>
              </>
            )}
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유일</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">열람일</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">신청자</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">신청일시</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">메모</th>
            <th
              className="whitespace-nowrap px-3 py-2 font-medium"
              title="카카오톡 공유 웹훅 기준 실제 전달 시각"
            >
              <span className="inline-flex items-center gap-1">
                <ShareChannelIcon channel={ShareChannel.KakaoTalk} className="h-3 w-3" />
                <span>카톡전송일시</span>
              </span>
            </th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">확인일시</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">수신번호</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">재공유이력</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">환불 · 연장</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedCoupons.map((c) => (
            <CouponRow
              key={c._id}
              coupon={c}
              embed={giftItemEmbedMap.get(c.giftItem)}
              canCancel={canCancel}
              hasCancellableCoupons={hasCancellableCoupons}
              disposeMode={disposeMode}
              isSelected={selectedIds.has(c._id)}
              isDisposeSelected={disposeSelectedIds.has(c._id)}
              isPromoting={isPromoting}
              isCrossCouponOverlap={crossCouponOverlapIds?.has(c._id) ?? false}
              hideDates={hideDates}
              toggleSelect={toggleSelect}
              toggleDisposeSelect={toggleDisposeSelect}
              onOpenShareHistory={onOpenShareHistory}
              handlePromoteToReissued={handlePromoteToReissued}
              onIssueRefund={onIssueRefund}
              onIssueRenewal={onIssueRenewal}
              onCancelIssue={onCancelIssue}
              onOpenKakaoShareCallbacks={onOpenKakaoShareCallbacks}
              isIssuing={isIssuing}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
